/* ============================================================
   PSYCHED — Polo PDP behaviour  ·  assets/polo.js
   No dependencies (Three.js loaded separately, only if the story
   section enables the live fabric). All handlers are id/data driven
   so sections stay independent and reorderable.

   SHOPFLO FLOWS
   1. Buy Now   -> clear cart, add the variant, handleFloCheckoutBtn()
   2. Cart      -> /cart/add.js, then handleFloCartBtn()
   3. Checkout  -> handleFloCheckoutBtn()
   No redirects to /cart or Shopify checkout.
   ============================================================ */
(function(){
  'use strict';

  /* ============================================================
     SHOPFLO BRIDGE
     Shopflo's script often loads after the theme, so handlers are
     resolved lazily. If one never arrives we fall back to native
     behaviour rather than dead-ending the click.
     ============================================================ */
  var FLO_BUY_NOW='handleFloBuyNowBtn',FLO_CART='handleFloCartBtn',FLO_CHECKOUT='handleFloCheckoutBtn';

  function floFn(name){return typeof window[name]==='function'?window[name]:null;}

  function whenFloReady(name,timeout){
    timeout=timeout||3000;
    return new Promise(function(resolve){
      var existing=floFn(name);
      if(existing)return resolve(existing);
      var start=Date.now();
      var poll=setInterval(function(){
        var fn=floFn(name);
        if(fn){clearInterval(poll);resolve(fn);}
        else if(Date.now()-start>timeout){clearInterval(poll);resolve(null);}
      },100);
    });
  }

  /* handleFloBuyNowBtn takes the event; the other two take none. */
  function callFlo(name,event,fallback){
    return whenFloReady(name).then(function(fn){
      if(!fn){
        if(window.console)console.warn('[polo] '+name+' unavailable, using fallback.');
        if(typeof fallback==='function')fallback();
        return false;
      }
      try{
        if(name===FLO_BUY_NOW)fn(event);else fn();
        return true;
      }catch(e){
        if(window.console)console.error('[polo] '+name+' threw:',e);
        if(typeof fallback==='function')fallback();
        return false;
      }
    });
  }

  /* ---------- cart primitives ---------- */
  function clearCart(){
    return fetch('/cart/clear.js',{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'}})
      .then(function(r){if(!r.ok)throw new Error('Could not clear the cart');return r.json();});
  }

  /* items: a single {id,quantity} or an array of them */
  function addToCart(items){
    var body=Array.isArray(items)?{items:items}:{id:Number(items.id),quantity:Number(items.quantity)||1};
    return fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify(body)})
      .then(function(r){
        if(!r.ok)return r.json().then(function(err){throw new Error(err.description||err.message||'Could not add to cart');});
        return r.json();
      });
  }

  function fetchCart(){
    return fetch('/cart.js',{headers:{'Accept':'application/json'}}).then(function(r){return r.json();}).catch(function(){return null;});
  }

  /* ---------- FLOW 2: cart ---------- */
  function openCart(event){
    if(event){event.preventDefault();event.stopPropagation();}
    return callFlo(FLO_CART,event,function(){
      // Same hook the theme CartNotification.open() uses for Shopflo.
      document.dispatchEvent(new CustomEvent('shopflo-event:add-to-cart'));
      var cart=document.querySelector('cart-notification');
      if(cart&&typeof cart.open==='function'){
        try{cart.open();}catch(err){}
        return;
      }
      window.location.href='/cart';
    });
  }

  /* Refresh theme minicart DOM + header count after a silent /cart/add.js */
  function refreshThemeCart(){
    return fetchCart().then(function(cart){
      if(!cart)return null;
      document.querySelectorAll('.cart-count').forEach(function(el){
        if(el.classList.contains('cart-count-drawer')){
          el.innerHTML='('+cart.item_count+')';
        }else{
          el.innerHTML=cart.item_count>100?'~':String(cart.item_count);
        }
      });
      var totalEl=document.querySelector('header-total-price');
      if(totalEl&&typeof totalEl.updateTotal==='function')totalEl.updateTotal(cart);
      var freeShip=document.querySelector('free-ship-progress-bar');
      if(freeShip&&typeof freeShip.init==='function')freeShip.init(cart.items_subtotal_price);
      return cart;
    });
  }

  /* Shared ATC used by the PDP button AND the sticky bar */
  function performAddToCart(opts){
    opts=opts||{};
    var variantId=opts.variantId;
    var quantity=Number(opts.quantity)||1;
    var buttons=[].concat(opts.buttons||[]).filter(Boolean);
    if(!variantId){toast('Please choose a size first');return Promise.resolve(false);}

    var originals=buttons.map(function(btn){
      var textEl=btn.querySelector('[data-atc-text]');
      return {btn:btn,textEl:textEl,html:textEl?textEl.textContent:btn.innerHTML,disabled:btn.disabled};
    });

    buttons.forEach(function(btn){
      btn.disabled=true;
      var textEl=btn.querySelector('[data-atc-text]');
      if(textEl)textEl.textContent='Adding…';
      else btn.textContent='Adding…';
    });

    return addToCart({id:variantId,quantity:quantity})
      .then(function(){
        originals.forEach(function(o){
          if(o.textEl)o.textEl.textContent='Added ✓';
          else o.btn.textContent='Added ✓';
        });
        return refreshThemeCart();
      })
      .then(function(){
        return openCart(null);
      })
      .catch(function(err){toast(err.message||'Could not add to cart');return false;})
      .then(function(res){
        setTimeout(function(){
          originals.forEach(function(o){
            o.btn.disabled=o.disabled;
            if(o.textEl)o.textEl.textContent=o.html;
            else o.btn.innerHTML=o.html;
          });
        },1400);
        return res;
      });
  }

  /* ---------- FLOW 3: checkout ---------- */
  function goToCheckout(event){
    if(event){event.preventDefault();event.stopPropagation();}
    return callFlo(FLO_CHECKOUT,event,function(){window.location.href='/checkout';});
  }

  /* ---------- FLOW 1: buy now ----------
     Clear first so checkout opens with exactly the clicked
     variant and nothing the shopper had lying in the bag. */
  function buyNow(event,variantId,quantity,btn){
    if(event){event.preventDefault();event.stopPropagation();}
    if(!variantId){toast('Please choose a size first');return Promise.resolve(false);}
    quantity=Number(quantity)||1;

    var orig=btn?btn.innerHTML:'';
    if(btn){btn.disabled=true;btn.innerHTML='<span>Processing…</span>';}
    function restore(){if(btn){btn.innerHTML=orig;btn.disabled=false;}}

    window.poloBuyNowContext={variantId:Number(variantId),quantity:quantity};

    return clearCart()
      .then(function(){return addToCart({id:variantId,quantity:quantity});})
      .then(function(){return fetchCart();})
      .then(function(){return callFlo(FLO_CHECKOUT,event,function(){window.location.href='/checkout';});})
      .then(function(res){restore();return res;})
      .catch(function(err){restore();toast(err.message||'Could not start checkout');return false;});
  }

  /* ---------- lightweight toast ---------- */
  function toast(msg){
    var el=document.getElementById('polo-toast');
    if(!el){
      el=document.createElement('div');el.id='polo-toast';
      el.setAttribute('style','position:fixed;left:50%;bottom:24px;transform:translateX(-50%) translateY(20px);background:#171717;color:#fff;padding:10px 18px;border-radius:999px;font-size:13px;opacity:0;pointer-events:none;transition:opacity .2s ease,transform .2s ease;z-index:99999');
      document.body.appendChild(el);
    }
    el.textContent=msg;
    requestAnimationFrame(function(){el.style.opacity='1';el.style.transform='translateX(-50%) translateY(0)';});
    clearTimeout(toast._t);
    toast._t=setTimeout(function(){el.style.opacity='0';el.style.transform='translateX(-50%) translateY(20px)';},2400);
  }

  /* ---------- gallery ---------- */
  function initGallery(root){
    var gallery=root.querySelector('[data-gallery]')||root;
    var slides=[].slice.call(root.querySelectorAll('.slide'));
    var thumbs=[].slice.call(root.querySelectorAll('.polo-thumb'));
    var dots=[].slice.call(root.querySelectorAll('.polo-dots i'));
    if(!slides.length)return;
    var cur=0;
    var autoplayOn=gallery.getAttribute('data-autoplay')==='true'&&slides.length>1;
    var speed=parseInt(gallery.getAttribute('data-autoplay-speed'),10)||4000;
    if(speed<1500)speed=1500;
    var timer=null;
    var reduceMotion=window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if(reduceMotion)autoplayOn=false;

    function keepThumbInRail(){
      var t=thumbs[cur];
      if(!t)return;
      var rail=t.closest('.polo-thumbs');
      if(!rail||rail.offsetParent===null)return; /* hidden on mobile / off-layout */
      /* Only nudge the thumb column — never scroll the page (scrollIntoView would). */
      var stage=root.querySelector('.polo-stage');
      if(stage){
        var sr=stage.getBoundingClientRect();
        if(sr.bottom<=0||sr.top>=(window.innerHeight||0))return;
      }
      var rr=rail.getBoundingClientRect(),tr=t.getBoundingClientRect();
      if(tr.top<rr.top)rail.scrollTop-=(rr.top-tr.top)+6;
      else if(tr.bottom>rr.bottom)rail.scrollTop+=(tr.bottom-rr.bottom)+6;
    }
    function go(i){cur=(i+slides.length)%slides.length;
      slides.forEach(function(s,x){s.classList.toggle('show',x===cur);});
      thumbs.forEach(function(t,x){t.classList.toggle('active',x===cur);});
      dots.forEach(function(d,x){d.classList.toggle('on',x===cur);});
      keepThumbInRail();
    }
    function stopAuto(){if(timer){clearInterval(timer);timer=null;}}
    function startAuto(){
      stopAuto();
      if(!autoplayOn)return;
      var lb=document.getElementById('polo-lightbox');
      if(lb&&lb.classList.contains('open'))return;
      timer=setInterval(function(){go(cur+1);},speed);
    }
    function bump(){stopAuto();startAuto();}

    thumbs.forEach(function(t,i){t.addEventListener('click',function(){go(i);bump();});});
    dots.forEach(function(d,i){d.addEventListener('click',function(){go(i);bump();});});
    var prev=root.querySelector('[data-gallery-prev]'),next=root.querySelector('[data-gallery-next]');
    if(prev)prev.addEventListener('click',function(){go(cur-1);bump();});
    if(next)next.addEventListener('click',function(){go(cur+1);bump();});
    var stage=root.querySelector('.polo-stage');
    if(stage){var sx=0;
      stage.addEventListener('touchstart',function(e){sx=e.touches[0].clientX;stopAuto();},{passive:true});
      stage.addEventListener('touchend',function(e){var dx=e.changedTouches[0].clientX-sx;if(Math.abs(dx)>40)go(cur+(dx<0?1:-1));bump();});
      stage.addEventListener('mouseenter',stopAuto);
      stage.addEventListener('mouseleave',startAuto);
      stage.addEventListener('focusin',stopAuto);
      stage.addEventListener('focusout',startAuto);
    }

    /* ---- click-to-zoom lightbox ---- */
    function srcOf(i){var im=slides[i]&&slides[i].querySelector('img');return im?(im.currentSrc||im.getAttribute('src')):'';}
    var lb=document.getElementById('polo-lightbox');
    if(!lb){
      lb=document.createElement('div');lb.id='polo-lightbox';lb.className='polo-lightbox';lb.setAttribute('aria-hidden','true');
      lb.innerHTML='<button type="button" class="lbx" aria-label="Close">✕</button><button type="button" class="lbnav lbprev" aria-label="Previous">‹</button><button type="button" class="lbnav lbnext" aria-label="Next">›</button><div class="lbstage"><img alt=""></div>';
      document.body.appendChild(lb);
    }
    var lbImg=lb.querySelector('img');
    function paint(){lbImg.classList.remove('zoom');lbImg.style.transformOrigin='center';lbImg.src=srcOf(cur);}
    function openLb(){stopAuto();paint();lb.querySelectorAll('.lbnav').forEach(function(b){b.style.display=slides.length>1?'':'none';});lb.classList.add('open');lb.setAttribute('aria-hidden','false');document.body.style.overflow='hidden';}
    function closeLb(){lb.classList.remove('open');lb.setAttribute('aria-hidden','true');document.body.style.overflow='';lbImg.classList.remove('zoom');startAuto();}
    lb.querySelector('.lbx').onclick=closeLb;
    lb.onclick=function(e){if(e.target===lb)closeLb();};
    lb.querySelector('.lbprev').onclick=function(e){e.stopPropagation();go(cur-1);paint();};
    lb.querySelector('.lbnext').onclick=function(e){e.stopPropagation();go(cur+1);paint();};
    lbImg.onclick=function(e){e.stopPropagation();lbImg.classList.toggle('zoom');};
    lbImg.onmousemove=function(e){if(!lbImg.classList.contains('zoom'))return;var r=lbImg.getBoundingClientRect();lbImg.style.transformOrigin=((e.clientX-r.left)/r.width*100)+'% '+((e.clientY-r.top)/r.height*100)+'%';};
    document.addEventListener('keydown',function(e){if(!lb.classList.contains('open'))return;if(e.key==='Escape')closeLb();else if(e.key==='ArrowLeft'){go(cur-1);paint();}else if(e.key==='ArrowRight'){go(cur+1);paint();}});
    slides.forEach(function(s){var im=s.querySelector('img');if(im)im.addEventListener('click',openLb);});

    document.addEventListener('visibilitychange',function(){if(document.hidden)stopAuto();else startAuto();});
    startAuto();

    root._goSlide=go;
  }

  /* ---------- style tiles (mini product images) ---------- */
  function initStyleTiles(root){
    var tiles=[].slice.call(root.querySelectorAll('.polo-stile:not(.more)'));
    var label=root.querySelector('[data-style-label]');
    tiles.forEach(function(t){
      t.addEventListener('click',function(e){
        var url=t.getAttribute('href')||t.getAttribute('data-url')||t.dataset.url;
        if(url){
          e.preventDefault();
          window.location.assign(url);
          return;
        }
        tiles.forEach(function(x){x.classList.remove('on');});
        t.classList.add('on');
        if(label&&t.dataset.name)label.textContent=t.dataset.name;
      });
    });

    /* drag-to-scroll — only suppress clicks after a real drag */
    var scroller=root.querySelector('[data-stiles-scroll]');
    if(!scroller)return;
    var down=false,dragging=false,startX=0,startLeft=0,moved=false;
    var DRAG_THRESHOLD=8;

    function onDown(e){
      if(e.button!=null&&e.button!==0)return;
      down=true;dragging=false;moved=false;
      startX=(e.touches?e.touches[0].pageX:e.pageX);
      startLeft=scroller.scrollLeft;
    }
    function onMove(e){
      if(!down)return;
      var x=(e.touches?e.touches[0].pageX:e.pageX);
      var dx=x-startX;
      if(!dragging&&Math.abs(dx)>DRAG_THRESHOLD){
        dragging=true;moved=true;
        scroller.classList.add('is-dragging');
      }
      if(!dragging)return;
      scroller.scrollLeft=startLeft-dx;
      if(e.cancelable)e.preventDefault();
    }
    function onUp(){
      if(!down)return;
      down=false;
      scroller.classList.remove('is-dragging');
      // Keep `moved` until the following click (capture) so we can cancel it once.
      setTimeout(function(){dragging=false;},0);
    }
    scroller.addEventListener('mousedown',onDown);
    window.addEventListener('mousemove',onMove);
    window.addEventListener('mouseup',onUp);
    scroller.addEventListener('touchstart',onDown,{passive:true});
    scroller.addEventListener('touchmove',onMove,{passive:false});
    scroller.addEventListener('touchend',onUp);
    scroller.addEventListener('click',function(e){
      if(moved){
        e.preventDefault();
        e.stopPropagation();
        moved=false;
      }
    },true);
  }

  /* ---------- variant / size picker ---------- */
  function money(cents,fmt){
    // Whole-rupee display (no trailing .00), grouped en-IN; keeps the currency prefix from fmt.
    try{
      var n=Math.round(cents/100).toLocaleString('en-IN');
      return (fmt||'₹{{amount}}').replace(/\{\{\s*amount[a-z_]*\s*\}\}/,n);
    }catch(e){return '₹'+Math.round(cents/100);}
  }
  function initProduct(root){
    var dataEl=root.querySelector('[data-product-json]');
    if(!dataEl)return;
    var parsed; try{parsed=JSON.parse(dataEl.textContent);}catch(e){return;}
    // data-product-json is `product.variants` (an array). Accept an object with .variants too.
    var variants=Array.isArray(parsed)?parsed:(parsed&&parsed.variants)||[];
    var fmt=root.getAttribute('data-money-format')||'₹{{amount}}';
    var idInput=root.querySelector('input[name="id"]');
    var atc=root.querySelector('[data-atc]');
    var stickyAtc=document.querySelector('#polo-sticky [data-sticky-atc]');
    var atcPrice=root.querySelector('[data-atc-price]');
    var priceEl=root.querySelector('[data-price]');
    var mrpEl=root.querySelector('[data-mrp]');
    var saveEl=root.querySelector('[data-save]');
    var sizeBtns=[].slice.call(root.querySelectorAll('.polo-size'));
    var qtyInput=root.querySelector('[data-qty]');
    var stkSize=document.querySelector('[data-stk-size]');
    var stkPrice=document.querySelector('[data-stk-price]');
    var optionIndex=parseInt(root.getAttribute('data-size-option-index')||'0',10);

    // Buy Now triggers, on the PDP and in the sticky bar.
    var buyBtns=[].slice.call(root.querySelectorAll('[data-buy-now]'))
      .concat([].slice.call(document.querySelectorAll('#polo-sticky [data-buy-now]')));

    var currentVariant=null;

    function variantForValue(val){
      return variants.filter(function(v){return v.options[optionIndex]===val;})[0];
    }
    function inr(n){return '₹'+Math.round(n).toLocaleString('en-IN');}
    function syncAutoPack(cents){
      var pick=root.querySelector('[data-packpick]');
      if(!pick)return;
      var pct=parseInt(pick.getAttribute('data-pack-discount')||'0',10);
      var rupees=Math.round(cents/100);
      var pay=pct?Math.round(rupees*(100-pct)/100):rupees;
      pick.querySelectorAll('[data-ppk]').forEach(function(card){
        var qty=parseInt(card.getAttribute('data-qty')||'1',10);
        var cur=card.querySelector('.cur');
        var per=card.querySelector('.per');
        if(qty<=1){
          card.setAttribute('data-price', inr(rupees));
          if(cur)cur.textContent=inr(rupees);
        }else if(pct){
          card.setAttribute('data-price', inr(rupees*qty));
          if(cur){
            cur.textContent=inr(rupees*qty);
            cur.classList.remove('is-was');
          }
          if(per)per.remove();
        }
      });
      var nudge=root.querySelector('[data-pack-nudge]');
      if(nudge)nudge.textContent='Buy any 2 @ '+inr(pay)+' each';
      var on=pick.querySelector('[data-ppk].on');
      if(on && parseInt(on.getAttribute('data-qty')||'1',10)>=2){
        var packPrice=on.getAttribute('data-price')||'';
        if(atcPrice)atcPrice.textContent=packPrice;
        if(stkPrice)stkPrice.textContent=packPrice;
      }
    }
    function setVariant(v){
      if(!v)return;
      currentVariant=v;
      if(idInput)idInput.value=v.id;
      var qty=qtyInput?Math.max(1,parseInt(qtyInput.value)||1):1;
      if(priceEl)priceEl.textContent=Math.round(v.price/100).toLocaleString('en-IN');
      if(atcPrice)atcPrice.textContent=money(v.price*qty,fmt);
      if(mrpEl){if(v.compare_at_price&&v.compare_at_price>v.price){mrpEl.textContent=money(v.compare_at_price,fmt);mrpEl.style.display='';if(saveEl){saveEl.textContent=Math.round((1-v.price/v.compare_at_price)*100)+'% OFF';saveEl.style.display='';}}else{mrpEl.style.display='none';if(saveEl)saveEl.style.display='none';}}
      if(stkPrice)stkPrice.textContent='₹'+Math.round(v.price/100).toLocaleString('en-IN');
      syncAutoPack(v.price);
      var label=v.available?(atc&&atc.getAttribute('data-label-add'))||'Add to Cart':(atc&&atc.getAttribute('data-label-sold'))||'Sold out';
      if(atc){
        atc.disabled=!v.available;
        var atcText=atc.querySelector('[data-atc-text]');
        if(atcText)atcText.textContent=label;
      }
      if(stickyAtc){
        stickyAtc.disabled=!v.available;
        var stickyText=stickyAtc.querySelector('[data-atc-text]');
        if(stickyText)stickyText.textContent=label;
        else stickyAtc.textContent=label;
      }
      // Keep every Buy Now trigger pointed at the live variant.
      buyBtns.forEach(function(b){b.setAttribute('data-variant-id',v.id);b.disabled=!v.available;});
    }
    function selectSize(val,btn){
      sizeBtns.forEach(function(b){b.classList.toggle('on',b===btn);});
      document.querySelectorAll('.polo-sticky-sizes button').forEach(function(b){b.classList.toggle('on',b.dataset.s===val);});
      root.querySelectorAll('[data-size-label]').forEach(function(e){e.textContent=val;});
      if(stkSize)stkSize.textContent=val;
      setVariant(variantForValue(val));
    }
    sizeBtns.forEach(function(b){b.addEventListener('click',function(){if(b.classList.contains('out'))return;selectSize(b.dataset.s,b);});});
    document.querySelectorAll('.polo-sticky-sizes button').forEach(function(b){b.addEventListener('click',function(){
      var match=sizeBtns.filter(function(x){return x.dataset.s===b.dataset.s;})[0];
      if(match&&!match.classList.contains('out'))selectSize(b.dataset.s,match);
    });});
    // qty
    root.querySelectorAll('[data-qty-step]').forEach(function(btn){btn.addEventListener('click',function(){
      var d=parseInt(btn.getAttribute('data-qty-step'),10);
      var v=Math.max(1,(parseInt(qtyInput.value)||1)+d);qtyInput.value=v;
      var onBtn=sizeBtns.filter(function(x){return x.classList.contains('on');})[0];
      var cur=onBtn?variantForValue(onBtn.dataset.s):null;
      if(cur&&atcPrice)atcPrice.textContent=money(cur.price*v,fmt);
    });});
    // preselect first available on-size button
    var pre=sizeBtns.filter(function(b){return b.classList.contains('on');})[0]||sizeBtns.filter(function(b){return !b.classList.contains('out');})[0];
    if(pre)selectSize(pre.dataset.s,pre);

    function liveQty(){return qtyInput?Math.max(1,parseInt(qtyInput.value)||1):1;}
    function liveVariantId(){
      if(idInput&&idInput.value)return idInput.value;
      return currentVariant?currentVariant.id:null;
    }
    root._liveVariantId=liveVariantId;
    root._liveQty=liveQty;
    // Lets the pack picker restore single-piece price/label on the shared ATC.
    root._refreshSingle=function(){if(currentVariant)setVariant(currentVariant);};

    /* ---- FLOW 2: add to cart -> same path for PDP + sticky ---- */
    function onAddClick(e){
      e.preventDefault();
      e.stopPropagation();
      var trigger=e.currentTarget;
      if(trigger.disabled)return;
      performAddToCart({
        variantId:liveVariantId(),
        quantity:liveQty(),
        buttons:[atc,stickyAtc]
      });
    }
    if(atc)atc.addEventListener('click',onAddClick);
    if(stickyAtc)stickyAtc.addEventListener('click',onAddClick);

    /* ---- FLOW 1: buy now -> clear, add, handleFloCheckoutBtn() ---- */
    buyBtns.forEach(function(b){
      b.addEventListener('click',function(e){
        buyNow(e,liveVariantId(),liveQty(),b);
      });
    });

    /* Stop the product form from ever posting to Shopify. */
    var form=idInput?idInput.closest('form'):root.querySelector('form[action*="/cart"]');
    if(form){
      form.addEventListener('submit',function(e){
        e.preventDefault();
        if(atc&&!atc.disabled)atc.click();
      });
    }
  }

  /* ---------- in-buy-box pack picker (Single / Pack of 2 / Pack of 3) ----------
     Single -> normal variant Add to Cart (initProduct handles it).
     Pack 2/3 -> the shared primary button opens the design picker modal
     with the right target count (window.poloOpenPack set by initBundle). */
  function initPackPick(root){
    var pick=root.querySelector('[data-packpick]');
    if(!pick)return;
    var cards=[].slice.call(pick.querySelectorAll('[data-ppk]'));
    if(!cards.length)return;
    var atc=root.querySelector('[data-atc]');
    var atcText=atc?atc.querySelector('[data-atc-text]'):null;
    var atcPrice=atc?atc.querySelector('[data-atc-price]'):null;
    var sep=atc?atc.querySelector('.polo-atc-sep'):null;
    var sizeWrap=root.querySelector('[data-size-wrap]');
    var qtyBox=root.querySelector('.polo-qty');
    var hint=root.querySelector('[data-pack-hint]');
    var hintQty=root.querySelector('[data-pack-hint-qty]');
    /* sticky bar mirrors the pack state */
    var sticky=document.getElementById('polo-sticky');
    var stickyAtc=sticky?sticky.querySelector('[data-sticky-atc]'):null;
    var stkText=stickyAtc?stickyAtc.querySelector('[data-atc-text]'):null;
    var stkPrice=document.querySelector('[data-stk-price]');
    var stkSizewrap=document.querySelector('[data-stk-sizewrap]');
    var stkPackname=document.querySelector('[data-stk-packname]');

    function selectCard(c,userClick){
      cards.forEach(function(x){var on=x===c;x.classList.toggle('on',on);x.setAttribute('aria-pressed',on?'true':'false');});
      var qty=parseInt(c.getAttribute('data-qty')||'1',10);
      var isPack=qty>=2;
      var packPrice=c.getAttribute('data-price')||'';
      var label=c.getAttribute('data-cta')||(isPack?('Pick any '+qty):'Add to Cart');
      root.setAttribute('data-pack-qty',qty);
      /* hide the single-size list for multi-packs (sizes are chosen per design in the popup),
         but keep the Size & fit guide link usable. */
      if(sizeWrap)sizeWrap.classList.toggle('is-pack',isPack);
      if(qtyBox)qtyBox.style.display=isPack?'none':'';
      if(hint){hint.hidden=!isPack;if(hintQty)hintQty.textContent=qty;}
      if(atc){
        if(isPack){
          atc.setAttribute('data-pack-mode','1');
          atc.disabled=false;
          if(atcText)atcText.textContent=label;
          if(atcPrice)atcPrice.textContent=packPrice;
          if(sep)sep.style.display='';
        }else{
          atc.removeAttribute('data-pack-mode');
          if(sep)sep.style.display='';
          if(root._refreshSingle)root._refreshSingle();   /* restores variant price + default label */
          if(atcText && c.getAttribute('data-cta'))atcText.textContent=label;
        }
      }
      /* mirror onto the sticky bar */
      if(sticky)sticky.classList.toggle('pack',isPack);
      if(isPack){
        if(stickyAtc)stickyAtc.setAttribute('data-pack-mode','1');
        if(stkText)stkText.textContent=label;
        if(stkPrice)stkPrice.textContent=packPrice;
        if(stkSizewrap)stkSizewrap.style.display='none';
        if(stkPackname)stkPackname.textContent=c.querySelector('.nm')?c.querySelector('.nm').textContent:('Pack of '+qty);
      }else{
        if(stickyAtc)stickyAtc.removeAttribute('data-pack-mode');
        if(stkSizewrap)stkSizewrap.style.display='';
        if(stkPackname)stkPackname.textContent='';
        if(root._refreshSingle)root._refreshSingle(); /* resets sticky price + label to the variant */
      }
    }
    /* Open the design picker with the selected pack size. */
    function openPack(){
      var qty=parseInt(root.getAttribute('data-pack-qty')||'2',10);
      if(typeof window.poloOpenPack==='function')window.poloOpenPack(qty);
      else{var band=document.getElementById('polo-bundle');if(band)band.scrollIntoView({behavior:'smooth',block:'start'});}
    }
    /* Clicking a pack card selects it; a multi-pack also opens the picker on its own. */
    cards.forEach(function(c){c.addEventListener('click',function(){
      selectCard(c,true);
      if(parseInt(c.getAttribute('data-qty')||'1',10)>=2)openPack();
    });});
    var init=cards.filter(function(c){return c.classList.contains('on');})[0]||cards[0];
    if(init)selectCard(init,false);
    /* Intercept the PDP + sticky buttons in pack mode (capture phase, before initProduct's add). */
    [atc,stickyAtc].forEach(function(btn){
      if(!btn)return;
      btn.addEventListener('click',function(e){
        if(btn.getAttribute('data-pack-mode')!=='1')return;
        e.preventDefault();e.stopImmediatePropagation();
        openPack();
      },true);
    });

    /* Price nudge: jump to the pack picker and pre-select that pack. */
    document.querySelectorAll('[data-ppk-jump]').forEach(function(j){
      j.addEventListener('click',function(e){
        e.preventDefault();
        var q=parseInt(j.getAttribute('data-ppk-jump')||'2',10);
        var target=cards.filter(function(c){return parseInt(c.getAttribute('data-qty')||'1',10)===q;})[0];
        if(target){selectCard(target,true);openPack();}
        else if(pick&&pick.scrollIntoView)pick.scrollIntoView({behavior:'smooth',block:'center'});
      });
    });
  }

  /* ---------- bundle-picker per-card image gallery (swipe + arrows + dots) ---------- */
  function initBBGalleries(){
    document.querySelectorAll('[data-bb-gal]').forEach(function(gal){
      var track=gal.querySelector('[data-bb-track]');
      if(!track)return;
      var slides=[].slice.call(track.querySelectorAll('.polo-bb-slide'));
      var dots=[].slice.call(gal.querySelectorAll('.polo-bb-dots i'));
      if(slides.length<2)return;
      function w(){return track.clientWidth||1;}
      function cur(){return Math.round(track.scrollLeft/w());}
      function sync(){var i=cur();dots.forEach(function(d,x){d.classList.toggle('on',x===i);});}
      function go(i){i=Math.max(0,Math.min(slides.length-1,i));track.scrollTo({left:i*w(),behavior:'smooth'});}
      var prev=gal.querySelector('[data-bb-prev]'),next=gal.querySelector('[data-bb-next]');
      if(prev)prev.addEventListener('click',function(e){e.preventDefault();e.stopPropagation();go(cur()-1);});
      if(next)next.addEventListener('click',function(e){e.preventDefault();e.stopPropagation();go(cur()+1);});
      track.addEventListener('scroll',function(){clearTimeout(track._t);track._t=setTimeout(sync,60);},{passive:true});
    });
  }

  /* ---------- pack tiers + design/size picker (capped to the selected pack size) ---------- */
  function initBundle(root){
    var tiers=[].slice.call(root.querySelectorAll('.polo-tier'));
    var addBtn=root.querySelector('[data-pack-add]');
    var baseLabel=addBtn?addBtn.textContent:'Choose your pack';
    var modal=document.getElementById('polo-bundle-modal');
    var target=1;

    function setLabel(q,pr){
      if(!addBtn)return;
      addBtn.textContent=baseLabel+(pr?(' · '+pr):'');
    }
    function selectTier(t){
      tiers.forEach(function(x){x.classList.remove('on');x.setAttribute('aria-pressed','false');});
      t.classList.add('on');t.setAttribute('aria-pressed','true');
      target=parseInt(t.getAttribute('data-qty')||'1',10);
      setLabel(target,t.getAttribute('data-price')||'');
    }
    tiers.forEach(function(t){t.addEventListener('click',function(){selectTier(t);});});
    var init=tiers.filter(function(t){return t.classList.contains('on');})[0]||tiers[0];
    if(init)selectTier(init);

    if(!modal){return;} // no picker present — nothing more to wire
    var cards=[].slice.call(modal.querySelectorAll('[data-bb-card]'));
    var count=modal.querySelector('[data-bb-count]'),checkout=modal.querySelector('[data-bb-checkout]'),hint=modal.querySelector('[data-bb-hint]');
    var emptyEl=modal.querySelector('[data-bb-empty]');

    function cardHasStock(card){
      var s=card.querySelector('[data-bb-variant]');
      if(!s||!s.options.length)return false;
      for(var i=0;i<s.options.length;i++){
        if(!s.options[i].disabled&&s.options[i].value)return true;
      }
      return false;
    }

    function variantIdFromCard(card){
      var s=card.querySelector('[data-bb-variant]');
      if(!s||!s.value)return null;
      var opt=s.options[s.selectedIndex];
      if(!opt||opt.disabled||!opt.value)return null;
      var id=parseInt(opt.value,10);
      return isNaN(id)?null:id;
    }

    function ensureAvailableSelection(card){
      var s=card.querySelector('[data-bb-variant]');
      if(!s)return false;
      var opt=s.options[s.selectedIndex];
      if(opt&&!opt.disabled&&opt.value)return true;
      for(var i=0;i<s.options.length;i++){
        if(!s.options[i].disabled&&s.options[i].value){
          s.selectedIndex=i;
          return true;
        }
      }
      return false;
    }

    // Drop / lock any card that has no sellable variant (stale cache, race, etc.)
    cards.forEach(function(card){
      if(!cardHasStock(card)){
        card.classList.add('soldout');
        card.setAttribute('data-available','false');
        var pick=card.querySelector('[data-bb-pick]');
        if(pick){pick.disabled=true;pick.textContent='Sold out';}
        var s=card.querySelector('[data-bb-variant]');
        if(s)s.disabled=true;
      }else{
        ensureAvailableSelection(card);
      }
    });
    cards=cards.filter(function(c){return c.getAttribute('data-available')!=='false';});

    function selected(){return cards.filter(function(c){return c.classList.contains('sel');});}
    function apply(){
      var n=selected().length, atCap=n>=target;
      cards.forEach(function(c){
        var pick=c.querySelector('[data-bb-pick]');if(!pick)return;
        var lock=atCap&&!c.classList.contains('sel');
        pick.disabled=lock; c.classList.toggle('locked',lock);
      });
      if(count)count.textContent=n+' of '+target;
      if(checkout){checkout.disabled=(n!==target||!cards.length);checkout.textContent='Add '+target+' to cart';}
      if(emptyEl)emptyEl.hidden=cards.length>0;
      if(addBtn)addBtn.disabled=cards.length===0;
    }
    function packOfferText(qty){
      var pick=document.querySelector('[data-packpick]');
      if(pick && parseInt(pick.getAttribute('data-pack-discount')||'0',10)>0) return '';
      var btn=document.querySelector('[data-ppk][data-qty="'+qty+'"]');
      var set=btn&&(btn.getAttribute('data-offer')||'').replace(/^\s+|\s+$/g,'');
      if(set)return set;
      var per=btn&&btn.querySelector('.per');
      var amount=per?(per.textContent||'').replace(/each/ig,'').replace(/[^0-9]/g,''):'';
      if(amount)return 'Buy any '+qty+' @ Rs'+amount+' each';
      if(qty===2)return 'Buy any 2 @ ₹999 each';
      if(qty===3)return 'Buy any 3 @ Rs833 each';
      return '';
    }
    function showPackOffer(qty){
      var el=modal.querySelector('[data-bb-offer]');
      if(!el)return;
      var text=packOfferText(qty);
      el.textContent=text;
      if(text)el.removeAttribute('hidden');
      else el.setAttribute('hidden','');
    }
    function open(){
      showPackOffer(target);
      if(!cards.length){
        toast('No in-stock designs available right now');
        return;
      }
      // reset any previous selection
      cards.forEach(function(c){
        c.classList.remove('sel');
        var p=c.querySelector('[data-bb-pick]');
        if(p){p.textContent='Add to pack';p.disabled=false;}
        ensureAvailableSelection(c);
      });
      var bbNoun=(modal.getAttribute('data-pack-noun')||'designs');
      if(hint)hint.textContent='Pick any '+target+' '+bbNoun+' and a size for each. Your pack price applies in cart.';
      showPackOffer(target);
      apply();
      modal.classList.add('open');modal.setAttribute('aria-hidden','false');document.body.style.overflow='hidden';
    }
    function close(){modal.classList.remove('open');modal.setAttribute('aria-hidden','true');document.body.style.overflow='';}
    if(addBtn)addBtn.addEventListener('click',open);
    // Let the in-buy-box pack picker open this modal with a given pack size.
    window.poloOpenPack=function(n){var q=parseInt(n,10);if(q&&q>0)target=q;open();};
    modal.querySelectorAll('[data-close-bundle]').forEach(function(b){b.addEventListener('click',close);});
    document.addEventListener('keydown',function(e){if(e.key==='Escape'&&modal.classList.contains('open'))close();});
    cards.forEach(function(card){
      var pick=card.querySelector('[data-bb-pick]');if(!pick)return;
      pick.addEventListener('click',function(){
        if(pick.disabled)return;
        if(!ensureAvailableSelection(card)){toast('This design is sold out');return;}
        card.classList.toggle('sel');
        pick.textContent=card.classList.contains('sel')?'Added ✓':'Add to pack';
        apply();
      });
      var sel=card.querySelector('[data-bb-variant]');
      if(sel)sel.addEventListener('change',function(){
        if(!ensureAvailableSelection(card)&&card.classList.contains('sel')){
          card.classList.remove('sel');
          pick.textContent='Add to pack';
          apply();
        }
      });
    });
    apply();

    /* ---- FLOW 2: pack add -> /cart/add.js -> handleFloCartBtn() ----
       Add data-bb-buy-now to this button to make it a Buy Now
       instead: the pack replaces the cart and checkout opens. */
    if(checkout)checkout.addEventListener('click',function(e){
      e.preventDefault();
      var sel=selected();
      if(sel.length!==target||!sel.length)return;
      var items=[];
      for(var i=0;i<sel.length;i++){
        var vid=variantIdFromCard(sel[i]);
        if(!vid){toast('Choose an in-stock size for each design');return;}
        items.push({id:vid,quantity:1});
      }
      if(items.length!==target){toast('Choose an in-stock size for each design');return;}

      var isBuyNow=checkout.hasAttribute('data-bb-buy-now');
      checkout.disabled=true;var label=checkout.textContent;checkout.textContent='Adding…';

      var chain=isBuyNow?clearCart():Promise.resolve();

      chain
        .then(function(){return addToCart(items);})
        .then(function(){return refreshThemeCart();})
        .then(function(){
          close();
          return isBuyNow?goToCheckout(null):openCart(null);
        })
        .catch(function(err){toast(err.message||'Could not add to cart');})
        .then(function(){checkout.disabled=false;checkout.textContent=label;apply();});
    });
  }

  /* ---------- carousels ---------- */
  function scrollRail(el,dir){el.scrollBy({left:dir*Math.min(el.clientWidth*0.8,380),behavior:'smooth'});}
  function initCaros(){
    document.querySelectorAll('[data-caro-prev]').forEach(function(b){b.addEventListener('click',function(){var r=document.getElementById(b.getAttribute('data-caro-prev'));if(r)scrollRail(r,-1);});});
    document.querySelectorAll('[data-caro-next]').forEach(function(b){b.addEventListener('click',function(){var r=document.getElementById(b.getAttribute('data-caro-next'));if(r)scrollRail(r,1);});});
  }

  /* ---------- size guide modal ---------- */
  function initModal(){
    var modal=document.getElementById('polo-size-guide');
    if(!modal){
      /* No size-guide section on the page — make the trigger still do something useful. */
      document.querySelectorAll('[data-open-size-guide]').forEach(function(b){b.addEventListener('click',function(e){
        e.preventDefault();
        var t=document.getElementById('polo-details')||document.getElementById('polo-reviews');
        if(t)t.scrollIntoView({behavior:'smooth',block:'start'});
      });});
      return;
    }
    function open(){modal.classList.add('open');modal.setAttribute('aria-hidden','false');document.body.style.overflow='hidden';}
    function close(){modal.classList.remove('open');modal.setAttribute('aria-hidden','true');document.body.style.overflow='';}
    document.querySelectorAll('[data-open-size-guide]').forEach(function(b){b.addEventListener('click',function(e){e.preventDefault();open();});});
    modal.querySelectorAll('[data-close-size-guide]').forEach(function(b){b.addEventListener('click',close);});
    document.addEventListener('keydown',function(e){if(e.key==='Escape')close();});
    // unit toggle
    var body=modal.querySelector('[data-size-body]');
    modal.querySelectorAll('.polo-unit-toggle button').forEach(function(b){b.addEventListener('click',function(){
      modal.querySelectorAll('.polo-unit-toggle button').forEach(function(x){x.classList.remove('on');});b.classList.add('on');
      var u=b.getAttribute('data-unit');
      modal.querySelectorAll('[data-in]').forEach(function(td){td.style.display=(u==='in')?'':'none';});
      modal.querySelectorAll('[data-cm]').forEach(function(td){td.style.display=(u==='cm')?'':'none';});
    });});
  }

  /* ---------- anchor scroll (rating -> reviews, etc.) ---------- */
  function initScrollLinks(){
    document.querySelectorAll('[data-scroll]').forEach(function(a){a.addEventListener('click',function(e){
      var t=document.getElementById(a.getAttribute('data-scroll'));if(t){e.preventDefault();t.scrollIntoView({behavior:'smooth',block:'start'});}
    });});
  }

  /* ---------- sticky ATC ---------- */
  function initSticky(){
    var sticky=document.getElementById('polo-sticky');
    var anchor=document.querySelector('[data-sticky-anchor]');
    if(!sticky||!anchor||!('IntersectionObserver'in window))return;
    new IntersectionObserver(function(e){var x=e[0];sticky.classList.toggle('show',!x.isIntersecting&&x.boundingClientRect.top<0);},{threshold:0}).observe(anchor);
  }

  /* ============================================================
     GLOBAL FLOW ROUTING
     Catches cart and checkout triggers anywhere on the page —
     header cart icon, drawer, cart page. Add your own selectors
     here rather than in the listener.
     ============================================================ */
  var SELECTORS={
    buyNow:['[data-buy-now]','[data-flo-buy-now]','.shopify-payment-button__button'].join(', '),
    cart:['[data-open-cart]','[data-flo-cart]','#cart-icon-bubble','.polo-cart-link','a[href="/cart"]','a[href$="/cart"]'].join(', '),
    checkout:['[data-checkout]','[data-flo-checkout]','button[name="checkout"]','input[name="checkout"]','#CartDrawer-Checkout','a[href="/checkout"]','a[href$="/checkout"]'].join(', ')
  };

  function closestMatch(t,sel){return t&&t.closest?t.closest(sel):null;}

  function initFlowRouting(){
    document.addEventListener('click',function(e){
      // Buy Now inside a PDP root is handled by initProduct (it knows
      // the live variant); only catch strays here.
      var buyEl=closestMatch(e.target,SELECTORS.buyNow);
      if(buyEl&&!buyEl.closest('[data-polo-product]')){
        buyNow(e,buyEl.getAttribute('data-variant-id'),buyEl.getAttribute('data-quantity')||1,buyEl);
        return;
      }

      var checkoutEl=closestMatch(e.target,SELECTORS.checkout);
      if(checkoutEl){goToCheckout(e);return;}

      var cartEl=closestMatch(e.target,SELECTORS.cart);
      if(cartEl){openCart(e);return;}
    },true);

    // A cart form submitted via its checkout button.
    document.addEventListener('submit',function(e){
      if(e.submitter&&closestMatch(e.submitter,SELECTORS.checkout)){
        e.preventDefault();
        goToCheckout(e);
      }
    },true);
  }

  /* ---------- recently viewed (localStorage of handles + /products/{h}.js) ---------- */
  function initRecently(){
    var section=document.getElementById('polo-recently');
    if(!section)return;
    var rail=section.querySelector('[data-recent-inner]');
    var handle=section.getAttribute('data-current-handle');
    var fmt=section.getAttribute('data-money-format')||'₹{{amount}}';
    var max=parseInt(section.getAttribute('data-limit')||'6',10);
    var key='polo_recent',list=[];
    try{list=JSON.parse(localStorage.getItem(key)||'[]');}catch(e){}
    var toShow=list.filter(function(h){return h!==handle;}).slice(0,max);

    function imageSrc(p){
      var src=p.featured_image||(p.images&&p.images[0])||'';
      if(!src)return '';
      if(typeof src==='object')src=src.src||src||'';
      if(!src)return '';
      src=String(src);
      // Shopify CDN accepts width=; keep existing query string intact.
      var sep=src.indexOf('?')>=0?'&':'?';
      if(!/[?&]width=\d+/i.test(src))src=src+sep+'width=600';
      return src;
    }

    function escapeAttr(s){
      return String(s||'').replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;');
    }

    if(toShow.length&&rail){
      Promise.all(toShow.map(function(h){return fetch('/products/'+encodeURIComponent(h)+'.js').then(function(r){return r.ok?r.json():null;}).catch(function(){return null;});}))
      .then(function(items){
        var html='';
        items.filter(function(p){return p&&imageSrc(p);}).forEach(function(p){
          var src=imageSrc(p);
          var img='<img src="'+escapeAttr(src)+'" loading="lazy" alt="'+escapeAttr(p.title)+'" width="600" height="800">';
          var was=(p.compare_at_price&&p.compare_at_price>p.price)?('<span class="was">'+money(p.compare_at_price,fmt)+'</span>'):'';
          html+='<div class="polo-card"><a class="img" href="'+escapeAttr(p.url)+'">'+img+'</a><h4><a href="'+escapeAttr(p.url)+'">'+escapeAttr(p.title)+'</a></h4><div class="cardpr"><span class="now">'+money(p.price,fmt)+'</span>'+was+'</div></div>';
        });
        if(html){
          rail.innerHTML=html;
          section.hidden=false;
          section.setAttribute('aria-hidden','false');
        }
      });
    }
    // record current for next time
    if(handle){list=list.filter(function(h){return h!==handle;});list.unshift(handle);list=list.slice(0,12);try{localStorage.setItem(key,JSON.stringify(list));}catch(e){}}
  }

  /* ---------- Three.js live fabric (optional) ---------- */
  var FABRIC_DEFAULT={color:0x2b2620,highlight:0xB4472B,weaveDensity:26,rippleAmp:0.16,rippleSpeed:0.9,dragInfluence:0.6};
  function initFabric(){
    var canvas=document.getElementById('polo-fabric-canvas');
    if(!canvas||typeof THREE==='undefined')return;
    var cfg=Object.assign({},FABRIC_DEFAULT);
    try{cfg=Object.assign(cfg,JSON.parse(canvas.getAttribute('data-config')||'{}'));}catch(e){}
    var r=new THREE.WebGLRenderer({canvas:canvas,antialias:true,alpha:true});
    r.setPixelRatio(Math.min(window.devicePixelRatio,2));
    var scene=new THREE.Scene();
    var cam=new THREE.PerspectiveCamera(38,1,0.1,100);cam.position.set(0,0,4.2);
    var geo=new THREE.PlaneGeometry(6,7,60,70);
    var base=geo.attributes.position.array.slice();
    var mat=new THREE.MeshStandardMaterial({color:cfg.color,roughness:.82,metalness:.05,side:THREE.DoubleSide});
    var mesh=new THREE.Mesh(geo,mat);mesh.rotation.x=-0.35;scene.add(mesh);
    var key=new THREE.DirectionalLight(0xffffff,1.15);key.position.set(-3,4,5);scene.add(key);
    var rim=new THREE.DirectionalLight(cfg.highlight,0.9);rim.position.set(4,-2,2);scene.add(rim);
    scene.add(new THREE.AmbientLight(0x40382f,0.7));
    var clock=new THREE.Clock(),ptr={x:0,y:0,tx:0,ty:0},wrap=canvas.parentElement;
    wrap.addEventListener('pointermove',function(e){var b=wrap.getBoundingClientRect();ptr.tx=((e.clientX-b.left)/b.width-.5)*2;ptr.ty=((e.clientY-b.top)/b.height-.5)*2;});
    wrap.addEventListener('pointerleave',function(){ptr.tx=0;ptr.ty=0;});
    function size(){var w=canvas.clientWidth||340,h=canvas.clientHeight||420;r.setSize(w,h,false);cam.aspect=w/h;cam.updateProjectionMatrix();}
    function anim(){requestAnimationFrame(anim);
      var t=clock.getElapsedTime()*cfg.rippleSpeed,p=geo.attributes.position.array,d=cfg.weaveDensity;
      ptr.x+=(ptr.tx-ptr.x)*.06;ptr.y+=(ptr.ty-ptr.y)*.06;
      for(var i=0;i<p.length;i+=3){var x=base[i],y=base[i+1];
        var weave=Math.sin(x*d)*Math.sin(y*d)*0.03;
        var wave=Math.sin(x*1.1+t)*Math.cos(y*1.0+t*.8)*cfg.rippleAmp;
        var lift=Math.exp(-((x/6-ptr.x)*(x/6-ptr.x)+(y/6+ptr.y)*(y/6+ptr.y))*3)*cfg.dragInfluence;
        p[i+2]=base[i+2]+weave+wave+lift;}
      geo.attributes.position.needsUpdate=true;geo.computeVertexNormals();
      mesh.rotation.z=ptr.x*0.05;mesh.rotation.x=-0.35+ptr.y*0.05;
      r.render(scene,cam);}
    window.addEventListener('resize',size);size();anim();
  }
  window.PoloInitFabric=initFabric;

  /* ---------- fabric strip pagination ---------- */
  function initQualDots(){
    document.querySelectorAll('.polo-qualstrip').forEach(function(strip){
      var scroller=strip.querySelector('.polo-quals');
      var dots=strip.querySelector('[data-qual-dots]');
      if(!scroller||!dots||dots.getAttribute('data-ready')==='1')return;
      var tiles=[].slice.call(scroller.querySelectorAll('.polo-qual'));
      if(tiles.length<2)return;
      dots.setAttribute('data-ready','1');
      tiles.forEach(function(_,i){
        var dot=document.createElement('i');
        if(i===0)dot.className='is-on';
        dots.appendChild(dot);
      });
      var els=[].slice.call(dots.children);
      function sync(){
        var overflow=scroller.scrollWidth-scroller.clientWidth>8;
        if(overflow)dots.removeAttribute('hidden');
        else dots.setAttribute('hidden','');
        if(!overflow)return;
        var w=tiles[0].getBoundingClientRect().width||1;
        var i=Math.round(scroller.scrollLeft/w);
        if(i<0)i=0;
        if(i>els.length-1)i=els.length-1;
        els.forEach(function(d,x){d.classList.toggle('is-on',x===i);});
      }
      scroller.addEventListener('scroll',function(){clearTimeout(scroller._qd);scroller._qd=setTimeout(sync,40);},{passive:true});
      window.addEventListener('resize',sync);
      sync();
    });
  }

  /* ---------- reviews slider pagination ---------- */
  function initRevDots(){
    document.querySelectorAll('#polo-reviews').forEach(function(section){
      var scroller=section.querySelector('[data-rev-cards]');
      var dots=section.querySelector('[data-rev-dots]');
      if(!scroller||!dots||dots.getAttribute('data-ready')==='1')return;
      var cards=[].slice.call(scroller.querySelectorAll('.polo-rev-card'));
      if(cards.length<2)return;
      dots.setAttribute('data-ready','1');
      cards.forEach(function(_,i){
        var dot=document.createElement('i');
        if(i===0)dot.className='is-on';
        dots.appendChild(dot);
      });
      var els=[].slice.call(dots.children);
      function sync(){
        var overflow=scroller.scrollWidth-scroller.clientWidth>8;
        if(overflow)dots.removeAttribute('hidden');
        else dots.setAttribute('hidden','');
        if(!overflow)return;
        var w=(cards[0].getBoundingClientRect().width||1)+12;
        var i=Math.round(scroller.scrollLeft/w);
        if(i<0)i=0;
        if(i>els.length-1)i=els.length-1;
        els.forEach(function(d,x){d.classList.toggle('is-on',x===i);});
      }
      scroller.addEventListener('scroll',function(){clearTimeout(scroller._rd);scroller._rd=setTimeout(sync,40);},{passive:true});
      window.addEventListener('resize',sync);
      sync();
    });
  }

  /* ---------- exposed helpers ---------- */
  window.poloOpenCart=openCart;
  window.poloAddToCart=performAddToCart;
  window.poloCheckout=goToCheckout;
  window.poloBuyNow=buyNow;

  /* ---------- boot (each step isolated so one failure can't disable the rest) ---------- */
  function safe(fn){try{fn();}catch(e){if(window.console)console.warn('[polo]',e);}}
  function boot(){
    document.querySelectorAll('[data-polo-product]').forEach(function(root){
      safe(function(){initGallery(root);});
      safe(function(){initStyleTiles(root);});
      safe(function(){initProduct(root);});
      safe(function(){initPackPick(root);});
    });
    document.querySelectorAll('[data-polo-bundle]').forEach(function(el){safe(function(){initBundle(el);});});
    safe(initBBGalleries);
    safe(initQualDots);
    safe(initRevDots);
    safe(initCaros);safe(initModal);safe(initScrollLinks);safe(initSticky);safe(initFlowRouting);safe(initRecently);
    if(typeof THREE!=='undefined')safe(initFabric);
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot);else boot();
})();








// /* ============================================================
//    PSYCHED — Polo PDP behaviour  ·  assets/polo.js
//    No dependencies (Three.js loaded separately, only if the story
//    section enables the live fabric). All handlers are id/data driven
//    so sections stay independent and reorderable.
//    ============================================================ */
// (function(){
//   'use strict';

//   /* ---------- gallery ---------- */
//   function initGallery(root){
//     var slides=[].slice.call(root.querySelectorAll('.slide'));
//     var thumbs=[].slice.call(root.querySelectorAll('.polo-thumb'));
//     var dots=[].slice.call(root.querySelectorAll('.polo-dots i'));
//     if(!slides.length)return;
//     var cur=0;
//     function go(i){cur=(i+slides.length)%slides.length;
//       slides.forEach(function(s,x){s.classList.toggle('show',x===cur);});
//       thumbs.forEach(function(t,x){t.classList.toggle('active',x===cur);});
//       dots.forEach(function(d,x){d.classList.toggle('on',x===cur);});}
//     thumbs.forEach(function(t,i){t.addEventListener('click',function(){go(i);});});
//     dots.forEach(function(d,i){d.addEventListener('click',function(){go(i);});});
//     var prev=root.querySelector('[data-gallery-prev]'),next=root.querySelector('[data-gallery-next]');
//     if(prev)prev.addEventListener('click',function(){go(cur-1);});
//     if(next)next.addEventListener('click',function(){go(cur+1);});
//     var stage=root.querySelector('.polo-stage');
//     if(stage){var sx=0;
//       stage.addEventListener('touchstart',function(e){sx=e.touches[0].clientX;},{passive:true});
//       stage.addEventListener('touchend',function(e){var dx=e.changedTouches[0].clientX-sx;if(Math.abs(dx)>40)go(cur+(dx<0?1:-1));});}

//     /* ---- click-to-zoom lightbox ---- */
//     function srcOf(i){var im=slides[i]&&slides[i].querySelector('img');return im?(im.currentSrc||im.getAttribute('src')):'';}
//     var lb=document.getElementById('polo-lightbox');
//     if(!lb){
//       lb=document.createElement('div');lb.id='polo-lightbox';lb.className='polo-lightbox';lb.setAttribute('aria-hidden','true');
//       lb.innerHTML='<button type="button" class="lbx" aria-label="Close">✕</button><button type="button" class="lbnav lbprev" aria-label="Previous">‹</button><button type="button" class="lbnav lbnext" aria-label="Next">›</button><div class="lbstage"><img alt=""></div>';
//       document.body.appendChild(lb);
//     }
//     var lbImg=lb.querySelector('img');
//     function paint(){lbImg.classList.remove('zoom');lbImg.style.transformOrigin='center';lbImg.src=srcOf(cur);}
//     function openLb(){paint();lb.querySelectorAll('.lbnav').forEach(function(b){b.style.display=slides.length>1?'':'none';});lb.classList.add('open');lb.setAttribute('aria-hidden','false');document.body.style.overflow='hidden';}
//     function closeLb(){lb.classList.remove('open');lb.setAttribute('aria-hidden','true');document.body.style.overflow='';lbImg.classList.remove('zoom');}
//     lb.querySelector('.lbx').onclick=closeLb;
//     lb.onclick=function(e){if(e.target===lb)closeLb();};
//     lb.querySelector('.lbprev').onclick=function(e){e.stopPropagation();go(cur-1);paint();};
//     lb.querySelector('.lbnext').onclick=function(e){e.stopPropagation();go(cur+1);paint();};
//     lbImg.onclick=function(e){e.stopPropagation();lbImg.classList.toggle('zoom');};
//     lbImg.onmousemove=function(e){if(!lbImg.classList.contains('zoom'))return;var r=lbImg.getBoundingClientRect();lbImg.style.transformOrigin=((e.clientX-r.left)/r.width*100)+'% '+((e.clientY-r.top)/r.height*100)+'%';};
//     document.addEventListener('keydown',function(e){if(!lb.classList.contains('open'))return;if(e.key==='Escape')closeLb();else if(e.key==='ArrowLeft'){go(cur-1);paint();}else if(e.key==='ArrowRight'){go(cur+1);paint();}});
//     slides.forEach(function(s){var im=s.querySelector('img');if(im)im.addEventListener('click',openLb);});

//     root._goSlide=go;
//   }

//   /* ---------- style tiles (mini product images) ---------- */
//   function initStyleTiles(root){
//     var tiles=[].slice.call(root.querySelectorAll('.polo-stile:not(.more)'));
//     var label=root.querySelector('[data-style-label]');
//     tiles.forEach(function(t){
//       t.addEventListener('click',function(){
//         // linked products navigate to their own PDP
//         if(t.dataset.url){window.location.href=t.dataset.url;return;}
//         tiles.forEach(function(x){x.classList.remove('on');});
//         t.classList.add('on');
//         if(label&&t.dataset.name)label.textContent=t.dataset.name;
//       });
//     });
//   }

//   /* ---------- variant / size picker ---------- */
//   function money(cents,fmt){
//     // Whole-rupee display (no trailing .00), grouped en-IN; keeps the currency prefix from fmt.
//     try{
//       var n=Math.round(cents/100).toLocaleString('en-IN');
//       return (fmt||'₹{{amount}}').replace(/\{\{\s*amount[a-z_]*\s*\}\}/,n);
//     }catch(e){return '₹'+Math.round(cents/100);}
//   }
//   function initProduct(root){
//     var dataEl=root.querySelector('[data-product-json]');
//     if(!dataEl)return;
//     var parsed; try{parsed=JSON.parse(dataEl.textContent);}catch(e){return;}
//     // data-product-json is `product.variants` (an array). Accept an object with .variants too.
//     var variants=Array.isArray(parsed)?parsed:(parsed&&parsed.variants)||[];
//     var fmt=root.getAttribute('data-money-format')||'₹{{amount}}';
//     var idInput=root.querySelector('input[name="id"]');
//     var atc=root.querySelector('[data-atc]');
//     var atcPrice=root.querySelector('[data-atc-price]');
//     var priceEl=root.querySelector('[data-price]');
//     var mrpEl=root.querySelector('[data-mrp]');
//     var saveEl=root.querySelector('[data-save]');
//     var sizeBtns=[].slice.call(root.querySelectorAll('.polo-size'));
//     var qtyInput=root.querySelector('[data-qty]');
//     var stkSize=document.querySelector('[data-stk-size]');
//     var stkPrice=document.querySelector('[data-stk-price]');
//     var optionIndex=parseInt(root.getAttribute('data-size-option-index')||'0',10);

//     function variantForValue(val){
//       return variants.filter(function(v){return v.options[optionIndex]===val;})[0];
//     }
//     function setVariant(v){
//       if(!v)return;
//       if(idInput)idInput.value=v.id;
//       var qty=qtyInput?Math.max(1,parseInt(qtyInput.value)||1):1;
//       if(priceEl)priceEl.textContent=money(v.price,fmt);
//       if(atcPrice)atcPrice.textContent=money(v.price*qty,fmt);
//       if(mrpEl){if(v.compare_at_price&&v.compare_at_price>v.price){mrpEl.textContent=money(v.compare_at_price,fmt);mrpEl.style.display='';if(saveEl){saveEl.textContent=Math.round((1-v.price/v.compare_at_price)*100)+'% OFF';saveEl.style.display='';}}else{mrpEl.style.display='none';if(saveEl)saveEl.style.display='none';}}
//       if(stkPrice)stkPrice.textContent=money(v.price,fmt);
//       if(atc){atc.disabled=!v.available;atc.querySelector('[data-atc-text]')&&(atc.querySelector('[data-atc-text]').textContent=v.available?atc.getAttribute('data-label-add'):atc.getAttribute('data-label-sold'));}
//     }
//     function selectSize(val,btn){
//       sizeBtns.forEach(function(b){b.classList.toggle('on',b===btn);});
//       document.querySelectorAll('.polo-sticky-sizes button').forEach(function(b){b.classList.toggle('on',b.dataset.s===val);});
//       root.querySelectorAll('[data-size-label]').forEach(function(e){e.textContent=val;});
//       if(stkSize)stkSize.textContent=val;
//       setVariant(variantForValue(val));
//     }
//     sizeBtns.forEach(function(b){b.addEventListener('click',function(){if(b.classList.contains('out'))return;selectSize(b.dataset.s,b);});});
//     document.querySelectorAll('.polo-sticky-sizes button').forEach(function(b){b.addEventListener('click',function(){
//       var match=sizeBtns.filter(function(x){return x.dataset.s===b.dataset.s;})[0];
//       if(match&&!match.classList.contains('out'))selectSize(b.dataset.s,match);
//     });});
//     // qty
//     root.querySelectorAll('[data-qty-step]').forEach(function(btn){btn.addEventListener('click',function(){
//       var d=parseInt(btn.getAttribute('data-qty-step'),10);
//       var v=Math.max(1,(parseInt(qtyInput.value)||1)+d);qtyInput.value=v;
//       var onBtn=sizeBtns.filter(function(x){return x.classList.contains('on');})[0];
//       var cur=onBtn?variantForValue(onBtn.dataset.s):null;
//       if(cur&&atcPrice)atcPrice.textContent=money(cur.price*v,fmt);
//     });});
//     // preselect first available on-size button
//     var pre=sizeBtns.filter(function(b){return b.classList.contains('on');})[0]||sizeBtns.filter(function(b){return !b.classList.contains('out');})[0];
//     if(pre)selectSize(pre.dataset.s,pre);
//   }

//   /* ---------- pack tiers + design/size picker (capped to the selected pack size) ---------- */
//   function initBundle(root){
//     var tiers=[].slice.call(root.querySelectorAll('.polo-tier'));
//     var addBtn=root.querySelector('[data-pack-add]');
//     var baseLabel=addBtn?addBtn.textContent:'Choose your pack';
//     var modal=document.getElementById('polo-bundle-modal');
//     var target=1;

//     function setLabel(q,pr){
//       if(!addBtn)return;
//       addBtn.textContent=baseLabel+(pr?(' · '+pr):'');
//     }
//     function selectTier(t){
//       tiers.forEach(function(x){x.classList.remove('on');x.setAttribute('aria-pressed','false');});
//       t.classList.add('on');t.setAttribute('aria-pressed','true');
//       target=parseInt(t.getAttribute('data-qty')||'1',10);
//       setLabel(target,t.getAttribute('data-price')||'');
//     }
//     tiers.forEach(function(t){t.addEventListener('click',function(){selectTier(t);});});
//     var init=tiers.filter(function(t){return t.classList.contains('on');})[0]||tiers[0];
//     if(init)selectTier(init);

//     if(!modal){return;} // no picker present — nothing more to wire
//     var cards=[].slice.call(modal.querySelectorAll('[data-bb-card]'));
//     var count=modal.querySelector('[data-bb-count]'),checkout=modal.querySelector('[data-bb-checkout]'),hint=modal.querySelector('[data-bb-hint]');
//     var baseHint=hint?hint.textContent:'';
//     function selected(){return cards.filter(function(c){return c.classList.contains('sel');});}
//     function apply(){
//       var n=selected().length, atCap=n>=target;
//       cards.forEach(function(c){
//         var pick=c.querySelector('[data-bb-pick]');if(!pick)return;
//         var lock=atCap&&!c.classList.contains('sel');
//         pick.disabled=lock; c.classList.toggle('locked',lock);
//       });
//       if(count)count.textContent=n+' of '+target;
//       if(checkout){checkout.disabled=(n!==target);checkout.textContent='Add '+target+' to cart';}
//     }
//     function open(){
//       // reset any previous selection
//       cards.forEach(function(c){c.classList.remove('sel');var p=c.querySelector('[data-bb-pick]');if(p)p.textContent='Add to pack';});
//       if(hint)hint.textContent='Pick '+target+' design'+(target>1?'s':'')+' and a size for each. '+baseHint;
//       apply();
//       modal.classList.add('open');modal.setAttribute('aria-hidden','false');document.body.style.overflow='hidden';
//     }
//     function close(){modal.classList.remove('open');modal.setAttribute('aria-hidden','true');document.body.style.overflow='';}
//     if(addBtn)addBtn.addEventListener('click',open);
//     modal.querySelectorAll('[data-close-bundle]').forEach(function(b){b.addEventListener('click',close);});
//     document.addEventListener('keydown',function(e){if(e.key==='Escape'&&modal.classList.contains('open'))close();});
//     cards.forEach(function(card){
//       var pick=card.querySelector('[data-bb-pick]');if(!pick)return;
//       pick.addEventListener('click',function(){
//         if(pick.disabled)return;
//         card.classList.toggle('sel');
//         pick.textContent=card.classList.contains('sel')?'Added ✓':'Add to pack';
//         apply();
//       });
//     });
//     if(checkout)checkout.addEventListener('click',function(){
//       var sel=selected();
//       if(sel.length!==target||!sel.length)return;
//       var items=[];sel.forEach(function(card){var s=card.querySelector('[data-bb-variant]');if(s&&s.value)items.push({id:parseInt(s.value,10),quantity:1});});
//       if(!items.length)return;
//       checkout.disabled=true;var label=checkout.textContent;checkout.textContent='Adding…';
//       fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:items})})
//         .then(function(r){return r.json();}).then(function(){window.location.href='/cart';})
//         .catch(function(){checkout.disabled=false;checkout.textContent=label;});
//     });
//   }

//   /* ---------- carousels ---------- */
//   function scrollRail(el,dir){el.scrollBy({left:dir*Math.min(el.clientWidth*0.8,380),behavior:'smooth'});}
//   function initCaros(){
//     document.querySelectorAll('[data-caro-prev]').forEach(function(b){b.addEventListener('click',function(){var r=document.getElementById(b.getAttribute('data-caro-prev'));if(r)scrollRail(r,-1);});});
//     document.querySelectorAll('[data-caro-next]').forEach(function(b){b.addEventListener('click',function(){var r=document.getElementById(b.getAttribute('data-caro-next'));if(r)scrollRail(r,1);});});
//   }

//   /* ---------- size guide modal ---------- */
//   function initModal(){
//     var modal=document.getElementById('polo-size-guide');
//     if(!modal)return;
//     function open(){modal.classList.add('open');modal.setAttribute('aria-hidden','false');document.body.style.overflow='hidden';}
//     function close(){modal.classList.remove('open');modal.setAttribute('aria-hidden','true');document.body.style.overflow='';}
//     document.querySelectorAll('[data-open-size-guide]').forEach(function(b){b.addEventListener('click',function(e){e.preventDefault();open();});});
//     modal.querySelectorAll('[data-close-size-guide]').forEach(function(b){b.addEventListener('click',close);});
//     document.addEventListener('keydown',function(e){if(e.key==='Escape')close();});
//     // unit toggle
//     var body=modal.querySelector('[data-size-body]');
//     modal.querySelectorAll('.polo-unit-toggle button').forEach(function(b){b.addEventListener('click',function(){
//       modal.querySelectorAll('.polo-unit-toggle button').forEach(function(x){x.classList.remove('on');});b.classList.add('on');
//       var u=b.getAttribute('data-unit');
//       modal.querySelectorAll('[data-in]').forEach(function(td){td.style.display=(u==='in')?'':'none';});
//       modal.querySelectorAll('[data-cm]').forEach(function(td){td.style.display=(u==='cm')?'':'none';});
//     });});
//   }

//   /* ---------- anchor scroll (rating -> reviews, etc.) ---------- */
//   function initScrollLinks(){
//     document.querySelectorAll('[data-scroll]').forEach(function(a){a.addEventListener('click',function(e){
//       var t=document.getElementById(a.getAttribute('data-scroll'));if(t){e.preventDefault();t.scrollIntoView({behavior:'smooth',block:'start'});}
//     });});
//   }

//   /* ---------- sticky ATC ---------- */
//   function initSticky(){
//     var sticky=document.getElementById('polo-sticky');
//     var anchor=document.querySelector('[data-sticky-anchor]');
//     if(!sticky||!anchor||!('IntersectionObserver'in window))return;
//     new IntersectionObserver(function(e){var x=e[0];sticky.classList.toggle('show',!x.isIntersecting&&x.boundingClientRect.top<0);},{threshold:0}).observe(anchor);
//   }

//   /* ---------- recently viewed (localStorage of handles + /products/{h}.js) ---------- */
//   function initRecently(){
//     var section=document.getElementById('polo-recently');
//     if(!section)return;
//     var rail=section.querySelector('[data-recent-inner]');
//     var handle=section.getAttribute('data-current-handle');
//     var fmt=section.getAttribute('data-money-format')||'₹{{amount}}';
//     var max=parseInt(section.getAttribute('data-limit')||'6',10);
//     var key='polo_recent',list=[];
//     try{list=JSON.parse(localStorage.getItem(key)||'[]');}catch(e){}
//     var toShow=list.filter(function(h){return h!==handle;}).slice(0,max);
//     if(toShow.length&&rail){
//       Promise.all(toShow.map(function(h){return fetch('/products/'+h+'.js').then(function(r){return r.ok?r.json():null;}).catch(function(){return null;});}))
//       .then(function(items){
//         var html='';
//         items.filter(Boolean).forEach(function(p){
//           var img=p.featured_image?('<img src="'+p.featured_image+'&width=500" loading="lazy" alt="'+(p.title||'')+'">'):'';
//           var was=(p.compare_at_price&&p.compare_at_price>p.price)?('<span class="was">'+money(p.compare_at_price,fmt)+'</span>'):'';
//           html+='<div class="polo-card" style="max-width:200px"><a class="img" href="'+p.url+'">'+img+'</a><h4><a href="'+p.url+'">'+p.title+'</a></h4><div class="cardpr"><span class="now">'+money(p.price,fmt)+'</span>'+was+'</div></div>';
//         });
//         if(html){rail.innerHTML=html;section.hidden=false;}
//       });
//     }
//     // record current for next time
//     if(handle){list=list.filter(function(h){return h!==handle;});list.unshift(handle);list=list.slice(0,12);try{localStorage.setItem(key,JSON.stringify(list));}catch(e){}}
//   }

//   /* ---------- Three.js live fabric (optional) ---------- */
//   var FABRIC_DEFAULT={color:0x2b2620,highlight:0xB4472B,weaveDensity:26,rippleAmp:0.16,rippleSpeed:0.9,dragInfluence:0.6};
//   function initFabric(){
//     var canvas=document.getElementById('polo-fabric-canvas');
//     if(!canvas||typeof THREE==='undefined')return;
//     var cfg=Object.assign({},FABRIC_DEFAULT);
//     try{cfg=Object.assign(cfg,JSON.parse(canvas.getAttribute('data-config')||'{}'));}catch(e){}
//     var r=new THREE.WebGLRenderer({canvas:canvas,antialias:true,alpha:true});
//     r.setPixelRatio(Math.min(window.devicePixelRatio,2));
//     var scene=new THREE.Scene();
//     var cam=new THREE.PerspectiveCamera(38,1,0.1,100);cam.position.set(0,0,4.2);
//     var geo=new THREE.PlaneGeometry(6,7,60,70);
//     var base=geo.attributes.position.array.slice();
//     var mat=new THREE.MeshStandardMaterial({color:cfg.color,roughness:.82,metalness:.05,side:THREE.DoubleSide});
//     var mesh=new THREE.Mesh(geo,mat);mesh.rotation.x=-0.35;scene.add(mesh);
//     var key=new THREE.DirectionalLight(0xffffff,1.15);key.position.set(-3,4,5);scene.add(key);
//     var rim=new THREE.DirectionalLight(cfg.highlight,0.9);rim.position.set(4,-2,2);scene.add(rim);
//     scene.add(new THREE.AmbientLight(0x40382f,0.7));
//     var clock=new THREE.Clock(),ptr={x:0,y:0,tx:0,ty:0},wrap=canvas.parentElement;
//     wrap.addEventListener('pointermove',function(e){var b=wrap.getBoundingClientRect();ptr.tx=((e.clientX-b.left)/b.width-.5)*2;ptr.ty=((e.clientY-b.top)/b.height-.5)*2;});
//     wrap.addEventListener('pointerleave',function(){ptr.tx=0;ptr.ty=0;});
//     function size(){var w=canvas.clientWidth||340,h=canvas.clientHeight||420;r.setSize(w,h,false);cam.aspect=w/h;cam.updateProjectionMatrix();}
//     function anim(){requestAnimationFrame(anim);
//       var t=clock.getElapsedTime()*cfg.rippleSpeed,p=geo.attributes.position.array,d=cfg.weaveDensity;
//       ptr.x+=(ptr.tx-ptr.x)*.06;ptr.y+=(ptr.ty-ptr.y)*.06;
//       for(var i=0;i<p.length;i+=3){var x=base[i],y=base[i+1];
//         var weave=Math.sin(x*d)*Math.sin(y*d)*0.03;
//         var wave=Math.sin(x*1.1+t)*Math.cos(y*1.0+t*.8)*cfg.rippleAmp;
//         var lift=Math.exp(-((x/6-ptr.x)*(x/6-ptr.x)+(y/6+ptr.y)*(y/6+ptr.y))*3)*cfg.dragInfluence;
//         p[i+2]=base[i+2]+weave+wave+lift;}
//       geo.attributes.position.needsUpdate=true;geo.computeVertexNormals();
//       mesh.rotation.z=ptr.x*0.05;mesh.rotation.x=-0.35+ptr.y*0.05;
//       r.render(scene,cam);}
//     window.addEventListener('resize',size);size();anim();
//   }
//   window.PoloInitFabric=initFabric;

//   /* ---------- boot (each step isolated so one failure can't disable the rest) ---------- */
//   function safe(fn){try{fn();}catch(e){if(window.console)console.warn('[polo]',e);}}
//   function boot(){
//     document.querySelectorAll('[data-polo-product]').forEach(function(root){
//       safe(function(){initGallery(root);});
//       safe(function(){initStyleTiles(root);});
//       safe(function(){initProduct(root);});
//     });
//     document.querySelectorAll('[data-polo-bundle]').forEach(function(el){safe(function(){initBundle(el);});});
//     safe(initCaros);safe(initModal);safe(initScrollLinks);safe(initSticky);safe(initRecently);
//     if(typeof THREE!=='undefined')safe(initFabric);
//   }
//   if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot);else boot();
// })();